export type GlossaryCategory =
  | "Painting & drawing"
  | "Printmaking"
  | "Photographic processes"
  | "Textile & fiber"
  | "Sculpture & ceramics"
  | "Digital & mixed media";

export type GlossarySubFamily =
  // Printmaking (5)
  | "printmaking-intaglio"
  | "printmaking-relief"
  | "printmaking-planographic"
  | "printmaking-screen"
  | "printmaking-unique"
  // Photographic processes (2)
  | "photo-alt-process"
  | "photo-digital-output"
  // Textile & fiber (3)
  | "textile-embroidery"
  | "textile-weaving"
  | "textile-applique"
  // Sculpture & ceramics (4)
  | "sculpture-clay-fire"
  | "sculpture-cast-metal"
  | "sculpture-papier-mache"
  | "sculpture-assemblage"
  // Digital & mixed media (4)
  | "digital-mixed-combine"
  | "digital-sculpture"
  | "digital-video-art"
  | "digital-papermaking"
  // Painting & drawing (5)
  | "paint-watercolor"
  | "paint-gouache"
  | "paint-acrylic"
  | "paint-oil"
  | "drawing-medium";

export type AnimationType =
  | "sweep"
  | "static-image"
  | "drawing-stroke";

export interface SubFamilyConfig {
  id: GlossarySubFamily;
  category: GlossaryCategory;
  animationType: AnimationType;
  accentColor: string;
  icons: string[];
  stageLabels?: [string, string, string];
  imagePath?: string;
  label?: string;
}

export interface GlossaryEntry {
  term: string;
  category: GlossaryCategory;
  subFamily: GlossarySubFamily;
  definition: string;
  aliases?: string[];
  imagePath?: string;
}

export const ACCENT_COLOR = "#4682B4";

export const SUBFAMILY_CONFIGS: Record<GlossarySubFamily, SubFamilyConfig> = {
  // --- Printmaking (5) ---
  "printmaking-intaglio": {
    id: "printmaking-intaglio",
    category: "Printmaking",
    animationType: "sweep",
    accentColor: ACCENT_COLOR,
    icons: ["pencil", "droplet", "stack-2"],
    stageLabels: ["Incise plate", "Ink, wipe clean", "Heavy press"],
  },
  "printmaking-relief": {
    id: "printmaking-relief",
    category: "Printmaking",
    animationType: "sweep",
    accentColor: ACCENT_COLOR,
    icons: ["cut", "droplet", "square"],
    stageLabels: ["Carve background", "Roll ink on top", "Stamp down"],
  },
  "printmaking-planographic": {
    id: "printmaking-planographic",
    category: "Printmaking",
    animationType: "sweep",
    accentColor: ACCENT_COLOR,
    icons: ["pencil", "flask", "photo"],
    stageLabels: ["Draw with grease", "Chemically treat", "Pull the print"],
  },
  "printmaking-screen": {
    id: "printmaking-screen",
    category: "Printmaking",
    animationType: "sweep",
    accentColor: ACCENT_COLOR,
    icons: ["grid-dots", "arrow-big-right", "photo"],
    stageLabels: ["Prepare stencil", "Squeegee through", "Lift, revealed"],
  },
  "printmaking-unique": {
    id: "printmaking-unique",
    category: "Printmaking",
    animationType: "sweep",
    accentColor: ACCENT_COLOR,
    icons: ["brush", "stack-2", "sparkles"],
    stageLabels: ["Build a plate", "Press once", "One of a kind"],
  },

  // --- Photographic processes (2) ---
  "photo-alt-process": {
    id: "photo-alt-process",
    category: "Photographic processes",
    animationType: "sweep",
    accentColor: ACCENT_COLOR,
    icons: ["flask", "sun", "photo"],
    stageLabels: ["Coat & treat", "Expose to light", "Image appears"],
  },
  "photo-digital-output": {
    id: "photo-digital-output",
    category: "Photographic processes",
    animationType: "sweep",
    accentColor: ACCENT_COLOR,
    icons: ["scan", "printer", "photo"],
    stageLabels: ["Capture digitally", "Print with inkjet", "Ready image"],
  },

  // --- Textile & fiber (3) ---
  "textile-embroidery": {
    id: "textile-embroidery",
    category: "Textile & fiber",
    animationType: "sweep",
    accentColor: ACCENT_COLOR,
    icons: ["circle-dot", "arrow-down", "shirt"],
    stageLabels: ["Thread needle", "Stitch through", "Pattern appears"],
  },
  "textile-weaving": {
    id: "textile-weaving",
    category: "Textile & fiber",
    animationType: "sweep",
    accentColor: ACCENT_COLOR,
    icons: ["grid-dots", "arrows-left-right", "shirt"],
    stageLabels: ["Set on loom", "Over, under, over", "Fabric forms"],
  },
  "textile-applique": {
    id: "textile-applique",
    category: "Textile & fiber",
    animationType: "sweep",
    accentColor: ACCENT_COLOR,
    icons: ["cut", "layout-grid", "shirt"],
    stageLabels: ["Cut pieces", "Layer onto base", "Stitch or glue"],
  },

  // --- Sculpture & ceramics (4) ---
  "sculpture-clay-fire": {
    id: "sculpture-clay-fire",
    category: "Sculpture & ceramics",
    animationType: "sweep",
    accentColor: ACCENT_COLOR,
    icons: ["hammer", "flame", "box"],
    stageLabels: ["Shape the clay", "Fire in a kiln", "Hardened form"],
  },
  "sculpture-cast-metal": {
    id: "sculpture-cast-metal",
    category: "Sculpture & ceramics",
    animationType: "sweep",
    accentColor: ACCENT_COLOR,
    icons: ["shape", "flame", "sparkles"],
    stageLabels: ["Shape wax model", "Melt out & pour", "Break mold, reveal"],
  },
  "sculpture-papier-mache": {
    id: "sculpture-papier-mache",
    category: "Sculpture & ceramics",
    animationType: "sweep",
    accentColor: ACCENT_COLOR,
    icons: ["droplet", "layout-grid", "sun"],
    stageLabels: ["Soak paper strips", "Layer while wet", "Dry hard"],
  },
  "sculpture-assemblage": {
    id: "sculpture-assemblage",
    category: "Sculpture & ceramics",
    animationType: "sweep",
    accentColor: ACCENT_COLOR,
    icons: ["layout-grid", "puzzle", "box"],
    stageLabels: ["Gather objects", "Arrange together", "Occupies space"],
  },

  // --- Digital & mixed media (4) ---
  "digital-mixed-combine": {
    id: "digital-mixed-combine",
    category: "Digital & mixed media",
    animationType: "sweep",
    accentColor: ACCENT_COLOR,
    icons: ["layout-grid", "puzzle", "photo"],
    stageLabels: ["Collect elements", "Combine together", "One unified piece"],
  },
  "digital-sculpture": {
    id: "digital-sculpture",
    category: "Digital & mixed media",
    animationType: "sweep",
    accentColor: ACCENT_COLOR,
    icons: ["cube", "rotate-3d", "printer-3d"],
    stageLabels: ["Model on screen", "Refine form", "Output & 3D print"],
  },
  "digital-video-art": {
    id: "digital-video-art",
    category: "Digital & mixed media",
    animationType: "sweep",
    accentColor: ACCENT_COLOR,
    icons: ["video", "scissors", "player-play"],
    stageLabels: ["Capture footage", "Edit sequence", "Plays on loop"],
  },
  "digital-papermaking": {
    id: "digital-papermaking",
    category: "Digital & mixed media",
    animationType: "sweep",
    accentColor: ACCENT_COLOR,
    icons: ["droplet", "square", "sun"],
    stageLabels: ["Pulp fibers", "Press into sheet", "Dry into paper"],
  },

  // --- Painting & drawing (5) ---
  "paint-watercolor": {
    id: "paint-watercolor",
    category: "Painting & drawing",
    animationType: "static-image",
    accentColor: ACCENT_COLOR,
    icons: [],
    imagePath: "/paint-stroke-assets/watercolour_steelblue.png",
    label: "Translucent wash & bloom",
  },
  "paint-gouache": {
    id: "paint-gouache",
    category: "Painting & drawing",
    animationType: "static-image",
    accentColor: ACCENT_COLOR,
    icons: [],
    imagePath: "/paint-stroke-assets/gouache_steelblue.png",
    label: "Smooth, flat, matte finish",
  },
  "paint-acrylic": {
    id: "paint-acrylic",
    category: "Painting & drawing",
    animationType: "static-image",
    accentColor: ACCENT_COLOR,
    icons: [],
    imagePath: "/paint-stroke-assets/acrylic_steelblue.png",
    label: "Opaque & fast-drying streaks",
  },
  "paint-oil": {
    id: "paint-oil",
    category: "Painting & drawing",
    animationType: "static-image",
    accentColor: ACCENT_COLOR,
    icons: [],
    imagePath: "/paint-stroke-assets/oil_steelblue.png",
    label: "Visible ridged, layered brush texture",
  },
  "drawing-medium": {
    id: "drawing-medium",
    category: "Painting & drawing",
    animationType: "drawing-stroke",
    accentColor: ACCENT_COLOR,
    icons: [],
    label: "Direct mark-making",
  },
};

// Backward-compatible category config helper
export const CATEGORY_CONFIGS = SUBFAMILY_CONFIGS;

export const GLOSSARY_DATA: Record<string, GlossaryEntry> = {
  // ==========================================
  // 1. Painting & Drawing
  // ==========================================
  "Watercolor": {
    term: "Watercolor",
    category: "Painting & drawing",
    subFamily: "paint-watercolor",
    imagePath: "/paint-stroke-assets/watercolour_steelblue.png",
    definition:
      "Paint made from pigment and water, applied to paper in thin, transparent layers. The white of the paper itself often shows through to create light areas.",
    aliases: ["Watercolour", "Watercolor on paper", "Watercolour on paper"],
  },
  "Gouache": {
    term: "Gouache",
    category: "Painting & drawing",
    subFamily: "paint-gouache",
    imagePath: "/paint-stroke-assets/gouache_steelblue.png",
    definition:
      "An opaque water-based paint, like watercolor's bolder sibling. It dries to a flat, matte finish and covers what's underneath completely, rather than staying see-through.",
    aliases: ["Gouache colour", "Gouache color", "Gouache on paper", "Gouache on cloth"],
  },
  "Tempera": {
    term: "Tempera",
    category: "Painting & drawing",
    subFamily: "paint-gouache",
    imagePath: "/paint-stroke-assets/gouache_steelblue.png",
    definition:
      "An older paint style made by mixing pigment with a binder like egg yolk. It dries fast and gives a matte, slightly luminous finish.",
    aliases: ["Egg tempera"],
  },
  "Acrylic": {
    term: "Acrylic",
    category: "Painting & drawing",
    subFamily: "paint-acrylic",
    imagePath: "/paint-stroke-assets/acrylic_steelblue.png",
    definition:
      "A fast-drying paint made with pigment suspended in a plastic-based liquid. It behaves like a flexible, quick version of oil paint and can be applied thick or thin.",
    aliases: ["Acrylic on canvas", "Acrylic on paper", "Acrylic paint", "Acrylic colour", "Acrylic color"],
  },
  "Enamel paint": {
    term: "Enamel paint",
    category: "Painting & drawing",
    subFamily: "paint-acrylic",
    imagePath: "/paint-stroke-assets/acrylic_steelblue.png",
    definition:
      "A hard, glossy paint originally made for metal and industrial surfaces, sometimes used by artists for its smooth, durable finish.",
    aliases: ["Enamel", "Enamel on paper", "Enamel on canvas"],
  },
  "Oil": {
    term: "Oil",
    category: "Painting & drawing",
    subFamily: "paint-oil",
    imagePath: "/paint-stroke-assets/oil_steelblue.png",
    definition:
      "Paint made by mixing pigment with oil, usually linseed. It dries slowly, which lets an artist blend colors smoothly and rework an area over days or weeks.",
    aliases: ["Oil paint", "Oil on canvas", "Oil on paper", "Oil on linen", "Oil colour", "Oil color"],
  },
  "Charcoal": {
    term: "Charcoal",
    category: "Painting & drawing",
    subFamily: "drawing-medium",
    definition:
      "Burnt wood, used as a drawing stick. It makes rich, smudgeable black marks and is one of the oldest drawing materials there is.",
    aliases: ["Charcoal on paper", "Charcoal drawing"],
  },
  "Graphite": {
    term: "Graphite",
    category: "Painting & drawing",
    subFamily: "drawing-medium",
    definition:
      "The material inside a pencil. Artists often use loose graphite sticks or powder for shading and fine detail work, not just pencils.",
    aliases: ["Graphite on paper", "Graphite pencil", "Graphite drawing"],
  },
  "Pastel": {
    term: "Pastel",
    category: "Painting & drawing",
    subFamily: "drawing-medium",
    definition:
      "Pigment pressed into stick form, used like a crayon. Soft pastels smudge easily for soft blending; oil pastels are stickier and more like a crayon.",
    aliases: ["Soft pastel", "Oil pastel", "Pastel on paper"],
  },
  "Conté": {
    term: "Conté",
    category: "Painting & drawing",
    subFamily: "drawing-medium",
    definition:
      "A drawing stick similar to a crayon, harder than charcoal, that comes in earthy tones like black, brown, and red.",
    aliases: ["Conte", "Conté crayon", "Conte crayon"],
  },
  "Pen and ink": {
    term: "Pen and ink",
    category: "Painting & drawing",
    subFamily: "drawing-medium",
    definition:
      "Drawing with ink applied through a pen nib or brush — capable of both fine lines and bold, filled-in shapes depending on pressure.",
    aliases: ["Pen & ink", "Ink drawing", "Indian ink", "India ink", "China ink", "Ink on paper"],
  },

  // ==========================================
  // 2. Printmaking (5 Sub-Families)
  // ==========================================
  // A. Intaglio
  "Etching": {
    term: "Etching",
    category: "Printmaking",
    subFamily: "printmaking-intaglio",
    definition:
      "Think of it like drawing with a needle instead of a pencil. The artist scratches a picture into a metal plate, ink fills those scratch marks, and the plate is pressed onto paper — like a stamp. Each one is printed by hand, one at a time.",
    aliases: ["Etching print", "Etching on paper", "Etching print on paper"],
  },
  "Aquatint": {
    term: "Aquatint",
    category: "Printmaking",
    subFamily: "printmaking-intaglio",
    definition:
      "A printmaking technique often paired with etching that creates soft, shaded areas of tone — like watercolor washes — instead of just lines.",
    aliases: ["Aquatint print"],
  },
  "Drypoint": {
    term: "Drypoint",
    category: "Printmaking",
    subFamily: "printmaking-intaglio",
    definition:
      "Similar to etching, but the artist scratches the metal plate directly with a sharp tool instead of using acid. This gives the printed lines a slightly fuzzy, soft edge.",
    aliases: ["Drypoint print"],
  },
  "Engraving": {
    term: "Engraving",
    category: "Printmaking",
    subFamily: "printmaking-intaglio",
    definition:
      "Cutting a design directly into a hard surface — metal or wood — with a sharp tool, then inking the grooves and pressing them onto paper.",
  },
  "Photogravure": {
    term: "Photogravure",
    category: "Printmaking",
    subFamily: "printmaking-intaglio",
    definition:
      "An early photographic printing process that etches a photograph onto a metal plate, then prints it with ink like an etching — giving photos a rich, velvety depth.",
  },
  "Chine collé": {
    term: "Chine collé",
    category: "Printmaking",
    subFamily: "printmaking-intaglio",
    definition:
      "A printmaking technique where a thin, often colored piece of paper is glued to the main sheet during printing, adding texture or a background tone to part of the image.",
    aliases: ["Chine colle", "Chine-colle", "Chine-collé"],
  },

  // B. Relief
  "Woodcut": {
    term: "Woodcut",
    category: "Printmaking",
    subFamily: "printmaking-relief",
    definition:
      "The artist carves away everything except the design from a block of wood, rolls ink onto the raised surface that's left, and presses it onto paper — like a rubber stamp, but hand-carved.",
    aliases: ["Woodblock print", "Woodblock", "Wood cut", "Woodcut on paper"],
  },
  "Linocut": {
    term: "Linocut",
    category: "Printmaking",
    subFamily: "printmaking-relief",
    definition:
      "The same idea as a woodcut, but carved into a softer sheet of linoleum instead of wood, which makes it easier to cut smooth, bold shapes.",
    aliases: ["Linoleum print", "Lino print", "Linocut on paper"],
  },

  // C. Planographic
  "Lithography": {
    term: "Lithography",
    category: "Printmaking",
    subFamily: "printmaking-planographic",
    definition:
      "A printing method based on the fact that oil and water don't mix. The design is drawn with a greasy crayon on a flat stone or metal plate, which is then inked and printed.",
    aliases: ["Lithograph", "Lithographic print"],
  },

  // D. Screen / Stencil
  "Serigraphy": {
    term: "Serigraphy",
    category: "Printmaking",
    subFamily: "printmaking-screen",
    definition:
      "Also called screen printing or silkscreen — ink is pushed through a fine mesh screen onto paper, with a stencil blocking the areas that should stay blank.",
    aliases: ["Serigraph", "Serigraph on glass", "Serigraph on paper"],
  },
  "Screen print": {
    term: "Screen print",
    category: "Printmaking",
    subFamily: "printmaking-screen",
    definition:
      "Also called screen printing or silkscreen — ink is pushed through a fine mesh screen onto paper, with a stencil blocking the areas that should stay blank.",
    aliases: ["Silkscreen", "Silk screen", "Screenprint", "Screen printing", "Screen print on paper"],
  },

  // E. Unique / Textural
  "Monotype": {
    term: "Monotype",
    category: "Printmaking",
    subFamily: "printmaking-unique",
    definition:
      "A one-of-a-kind print. The artist paints or inks a smooth surface, then presses paper onto it — since the ink transfers unevenly, no second copy comes out quite the same.",
  },
  "Monoprint": {
    term: "Monoprint",
    category: "Printmaking",
    subFamily: "printmaking-unique",
    definition:
      "Similar to a monotype but starting from a plate that has some fixed, repeatable elements — so each print shares a base but is never identical.",
  },
  "Collagraph": {
    term: "Collagraph",
    category: "Printmaking",
    subFamily: "printmaking-unique",
    definition:
      "A print made from a textured collage — the artist builds up a plate by gluing different materials to it, then inks and presses it, so the print captures that texture.",
    aliases: ["Collograph", "Collagraph print"],
  },
  "Viscosity printing": {
    term: "Viscosity printing",
    category: "Printmaking",
    subFamily: "printmaking-unique",
    definition:
      "A printmaking technique that gets several colors out of a single plate in one pass, by using inks of different thicknesses that settle into different depths of the carved plate instead of sitting on top of each other.",
  },

  // ==========================================
  // 3. Photographic & Light-Based Processes
  // ==========================================
  // A. Alt-Process / Light Exposure
  "Silver gelatin print": {
    term: "Silver gelatin print",
    category: "Photographic processes",
    subFamily: "photo-alt-process",
    definition:
      "The classic black-and-white photograph — printed by exposing light-sensitive paper in a darkroom, the way film photography worked for most of the 20th century.",
    aliases: ["Gelatin silver print"],
  },
  "Cyanotype": {
    term: "Cyanotype",
    category: "Photographic processes",
    subFamily: "photo-alt-process",
    definition:
      "A photographic process that produces a striking Prussian-blue image. Paper is coated in light-sensitive chemicals, then exposed to sunlight — often with objects placed directly on it to leave a silhouette.",
    aliases: ["Cyanotype print"],
  },
  "Salted paper print": {
    term: "Salted paper print",
    category: "Photographic processes",
    subFamily: "photo-alt-process",
    definition:
      "One of the very first ways people ever made photographs, long before phones or digital cameras. Paper is treated with salt and light-sensitive chemicals, then left in the sun until the image slowly appears.",
    aliases: ["Salt paper print", "Salt print", "Salted paper", "Gold chloride toned salt paper print"],
  },
  "Gum bichromate print": {
    term: "Gum bichromate print",
    category: "Photographic processes",
    subFamily: "photo-alt-process",
    definition:
      "A hand-applied photographic printing process using pigment mixed with a light-sensitive gum, giving photos a soft, painterly texture instead of a sharp photographic look.",
    aliases: ["Gum bichromate", "Gum print"],
  },
  "Van Dyke print": {
    term: "Van Dyke print",
    category: "Photographic processes",
    subFamily: "photo-alt-process",
    definition:
      "An early photographic printing process that produces warm, rich brown-toned images, named after the similar brown used in old master paintings.",
    aliases: ["Van Dyke brown print", "Vandyke print"],
  },
  "Photoetching": {
    term: "Photoetching",
    category: "Photographic processes",
    subFamily: "photo-alt-process",
    definition:
      "A process that transfers a photographic image onto a metal plate using light-sensitive chemicals, then prints it the way a regular etching is printed.",
    aliases: ["Photo-etching", "Photo etching"],
  },
  "Cuprotype": {
    term: "Cuprotype",
    category: "Photographic processes",
    subFamily: "photo-alt-process",
    definition:
      "An early photographic printing process similar to Van Dyke printing, using copper-based chemicals to produce warm, reddish-brown toned images.",
  },

  // B. Digital & Scanned Output
  "Digital print": {
    term: "Digital print",
    category: "Photographic processes",
    subFamily: "photo-digital-output",
    definition:
      "An image printed directly from a computer file using fine-art inkjet printers, often onto archival paper made to resist fading for decades.",
    aliases: ["Giclée", "Giclee", "Inkjet print", "Giclée print"],
  },
  "Archival print": {
    term: "Archival print",
    category: "Photographic processes",
    subFamily: "photo-digital-output",
    definition:
      "A print made with pigments and paper chosen specifically to resist fading, so the image stays vivid for a long time.",
    aliases: ["Archival pigment print"],
  },
  "Scanography": {
    term: "Scanography",
    category: "Photographic processes",
    subFamily: "photo-digital-output",
    definition:
      "Making an image by placing objects directly onto a flatbed scanner instead of using a camera — the scanner's own light and lens capture whatever is pressed against the glass in sharp, flat detail.",
  },

  // ==========================================
  // 4. Textile & Fiber (3 Sub-Families)
  // ==========================================
  "Embroidery": {
    term: "Embroidery",
    category: "Textile & fiber",
    subFamily: "textile-embroidery",
    definition:
      "Stitching thread or wool onto fabric with a needle to build up texture and pattern — the same craft used on clothes or cushions, used here as part of the artwork itself.",
    aliases: ["Hand embroidery", "Embroidered"],
  },
  "Weaving": {
    term: "Weaving",
    category: "Textile & fiber",
    subFamily: "textile-weaving",
    definition:
      "Interlacing threads over and under each other, usually on a loom, to build up a piece of fabric from scratch as the artwork.",
    aliases: ["Handwoven", "Woven textile"],
  },
  "Tapestry": {
    term: "Tapestry",
    category: "Textile & fiber",
    subFamily: "textile-weaving",
    definition:
      "A woven textile, often telling a scene or picture through the pattern of the weave itself rather than paint or ink.",
  },
  "Applique": {
    term: "Applique",
    category: "Textile & fiber",
    subFamily: "textile-applique",
    definition:
      "Sewing or gluing pieces of fabric onto a larger background fabric to build up a picture or pattern, layer by layer.",
    aliases: ["Appliqué"],
  },

  // ==========================================
  // 5. Sculpture & Ceramics (4 Sub-Families)
  // ==========================================
  "Terracotta": {
    term: "Terracotta",
    category: "Sculpture & ceramics",
    subFamily: "sculpture-clay-fire",
    definition:
      "Unglazed, reddish-brown clay, fired in a kiln until it hardens permanently — one of the oldest sculpting materials, still warm and earthy in tone.",
    aliases: ["Terra-cotta", "Terra cotta"],
  },
  "Ceramic": {
    term: "Ceramic",
    category: "Sculpture & ceramics",
    subFamily: "sculpture-clay-fire",
    definition:
      "Clay shaped and then fired at high heat until it hardens permanently, often finished with a glazed, glass-like coating.",
    aliases: ["Ceramics", "Glazed ceramic"],
  },
  "Stoneware": {
    term: "Stoneware",
    category: "Sculpture & ceramics",
    subFamily: "sculpture-clay-fire",
    definition:
      "A denser, sturdier type of fired clay than terracotta, fired at a higher temperature so it comes out tougher and less porous.",
  },
  "Bronze": {
    term: "Bronze",
    category: "Sculpture & ceramics",
    subFamily: "sculpture-cast-metal",
    definition:
      "A metal artwork, usually cast by pouring melted bronze into a mold made from an original sculpture — a technique used for thousands of years.",
    aliases: ["Bronze sculpture", "Cast bronze"],
  },
  "Dhokra": {
    term: "Dhokra",
    category: "Sculpture & ceramics",
    subFamily: "sculpture-cast-metal",
    definition:
      "A centuries-old Indian metal-casting technique: a wax model is coated in clay, then heated until the wax melts out and molten bronze or brass is poured into the hollow left behind. The clay mold is broken to release it, so every piece is one of a kind.",
  },
  "Papier-mâché": {
    term: "Papier-mâché",
    category: "Sculpture & ceramics",
    subFamily: "sculpture-papier-mache",
    definition:
      "Layers of paper soaked in glue or paste, built up and shaped while wet, then left to dry hard — a lightweight way to sculpt.",
    aliases: [
      "Papier-mache",
      "Papier mache",
      "Paper mache",
      "Paper-mache",
      "Papermache",
      "Paper mâché",
      "Paper-mâché",
      "Papier mâché",
    ],
  },
  "Assemblage": {
    term: "Assemblage",
    category: "Sculpture & ceramics",
    subFamily: "sculpture-assemblage",
    definition:
      "A sculpture built by combining found objects and materials — old tools, scrap metal, fabric — rather than carving or molding from scratch.",
  },
  "Installation": {
    term: "Installation",
    category: "Sculpture & ceramics",
    subFamily: "sculpture-assemblage",
    definition:
      "An artwork made to occupy and transform a physical space, often built from multiple objects or elements meant to be experienced together in that setting.",
    aliases: ["Site-specific installation", "Art installation"],
  },

  // ==========================================
  // 6. Digital & Mixed Media (4 Sub-Families)
  // ==========================================
  "Mixed media": {
    term: "Mixed media",
    category: "Digital & mixed media",
    subFamily: "digital-mixed-combine",
    definition:
      "An artwork that combines more than one material or technique — paint plus fabric, or found objects plus ink, for example — rather than sticking to just one.",
    aliases: ["Mixed-media"],
  },
  "Collage": {
    term: "Collage",
    category: "Digital & mixed media",
    subFamily: "digital-mixed-combine",
    definition:
      "An artwork built by gluing down separate flat pieces — paper, fabric, photographs — to form one combined image.",
  },
  "Digital sculpture": {
    term: "Digital sculpture",
    category: "Digital & mixed media",
    subFamily: "digital-sculpture",
    definition:
      "A three-dimensional form designed entirely on a computer, sometimes 3D printed or fabricated afterward, sometimes shown only as a digital image.",
    aliases: ["3D sculpture"],
  },
  "Video art": {
    term: "Video art",
    category: "Digital & mixed media",
    subFamily: "digital-video-art",
    definition:
      "A moving-image artwork, shown on a screen or projected, using video the way a painter would use canvas.",
    aliases: ["Video installation", "Moving image"],
  },
  "Papermaking": {
    term: "Papermaking",
    category: "Digital & mixed media",
    subFamily: "digital-papermaking",
    definition:
      "Making paper completely from scratch by pulping plant fibers — straw, bark, even old cloth — and pressing them into sheets. Sometimes the handmade paper itself is the finished artwork, not just something to draw or paint on.",
    aliases: ["Handmade paper", "Handmade paper sheet"],
  },
};
